import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faGlobe, faHeart, faSmile, faSadTear, faAngry, faLaugh, faThumbsUp, faPaperPlane, faSignInAlt, faUser, faComments, faComment, faReply, faEdit, faTrash, faSpinner, faTimes } from '@fortawesome/free-solid-svg-icons';
import { faDiscord } from '@fortawesome/free-brands-svg-icons';
import { supabase, DISCORD_WEBHOOK_URL, DISCORD_WEBHOOK_TOKEN } from '../utils/supabaseClient';
import { v4 as uuidv4 } from 'uuid';
import { theme } from '../styles/GlobalStyles';

// Componentes estilizados
const SecretsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 40px;
  color: ${theme.colors.primary};
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SecretForm = styled.form`
  background-color: ${theme.colors.backgroundAlt};
  padding: 30px;
  border-radius: 15px;
  margin-bottom: 40px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(to right, ${theme.colors.primary}, ${theme.colors.accent});
  }
`;

const FormTitle = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${theme.colors.secondary};
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 15px;
  border-radius: 10px;
  border: 1px solid ${theme.colors.border};
  background-color: ${theme.colors.background};
  color: ${theme.colors.secondary};
  font-size: 1rem;
  min-height: 120px;
  margin-bottom: 20px;
  resize: vertical;
  transition: border-color 0.3s;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 2px ${theme.colors.primary}30;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
`;

const Checkbox = styled.input`
  margin-right: 10px;
  cursor: pointer;
  width: 18px;
  height: 18px;
  appearance: none;
  -webkit-appearance: none;
  background-color: ${theme.colors.background};
  border: 1px solid ${props => props.checked ? theme.colors.primary : theme.colors.border};
  border-radius: 4px;
  outline: none;
  position: relative;
  transition: all 0.2s ease;
  
  &:checked {
    background-color: ${theme.colors.primary};
    border-color: ${theme.colors.primary};
    
    &::after {
      content: '✓';
      color: white;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-size: 12px;
    }
  }
  
  &:hover {
    border-color: ${theme.colors.primary};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CheckboxLabel = styled.label`
  font-size: 0.95rem;
  color: ${props => props.isChecked ? theme.colors.primary : theme.colors.textSecondary};
  cursor: pointer;
  user-select: none;
  display: flex;
  align-items: center;
  transition: color 0.2s ease;
  font-weight: ${props => props.isChecked ? '600' : '400'};
  
  &:hover {
    color: ${props => props.isChecked ? theme.colors.primary : theme.colors.secondary};
  }
`;

const NSFWIcon = styled.span`
  display: inline-block;
  color: ${theme.colors.primary};
  margin-right: 6px;
  font-weight: bold;
`;

const SubmitButton = styled.button`
  background-color: ${theme.colors.primary};
  color: white;
  border: none;
  border-radius: 50px;
  padding: 12px 30px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 10px;
  
  &:hover {
    background-color: ${theme.colors.accent};
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(255, 107, 107, 0.4);
  }
  
  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const LoginButton = styled.button`
  background-color: #5865F2;
  color: white;
  border: none;
  border-radius: 50px;
  padding: 12px 25px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 0 auto 20px;
  
  &:hover {
    background-color: #4752c4;
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(88, 101, 242, 0.3);
  }
`;

const UserInfoBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background-color: rgba(255, 255, 255, 0.05);
  padding: 8px 15px;
  border-radius: 30px;
  margin: 0 auto 20px;
  max-width: fit-content;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const UserAvatar = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid ${theme.colors.primary};
`;

const UserName = styled.span`
  color: ${theme.colors.secondary};
  font-size: 0.9rem;
  font-weight: 500;
`;

const SecretsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const SecretCard = styled.div`
  background-color: ${theme.colors.backgroundAlt};
  border-radius: 12px;
  padding: 25px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background: linear-gradient(to right, ${theme.colors.primary}80, transparent);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
    
    &::after {
      opacity: 1;
    }
  }
`;

const SecretContent = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 20px;
  color: ${theme.colors.secondary};
  white-space: pre-wrap;
  word-break: break-word;
`;

const SecretMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.9rem;
  color: ${theme.colors.textSecondary};
`;

const SecretDate = styled.span`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const ReactionBar = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 15px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 8px;
    justify-content: center;
    flex-direction: column;
  }
`;

const ReactionButton = styled.button`
  background-color: ${props => props.active ? theme.colors.primary + '30' : 'transparent'};
  color: ${props => props.active ? theme.colors.primary : theme.colors.textSecondary};
  border: 1px solid ${props => props.active ? theme.colors.primary : theme.colors.border};
  border-radius: 20px;
  padding: 5px 12px;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${theme.colors.primary}20;
    color: ${theme.colors.primary};
  }
  
  span {
    font-weight: ${props => props.active ? '600' : 'normal'};
  }
  
  @media (max-width: 768px) {
    justify-content: center;
    padding: 8px 15px;
    font-size: 0.95rem;
  }
`;

const NoSecrets = styled.div`
  text-align: center;
  padding: 40px;
  color: ${theme.colors.textSecondary};
  font-size: 1.2rem;
  background-color: rgba(46, 46, 46, 0.4);
  border-radius: 15px;
  border: 1px dashed rgba(255, 255, 255, 0.1);
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
`;

const ErrorMessage = styled.div`
  background-color: #ffebee;
  color: #c62828;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 0.95rem;
`;

const SuccessMessage = styled.div`
  background-color: #e8f5e9;
  color: #2e7d32;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 0.95rem;
`;

const LoadMoreButton = styled.button`
  background-color: transparent;
  border: 2px solid ${theme.colors.primary};
  color: ${theme.colors.primary};
  border-radius: 50px;
  padding: 10px 25px;
  font-size: 1rem;
  margin: 30px auto;
  display: block;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${theme.colors.primary}10;
    transform: translateY(-2px);
  }
  
  &:disabled {
    border-color: #cccccc;
    color: #999999;
    cursor: not-allowed;
    transform: none;
  }
`;

// Componentes estilizados para comentarios
const CommentSection = styled.div`
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid ${theme.colors.border};
`;

const CommentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const CommentTitle = styled.h3`
  font-size: 1.1rem;
  color: ${theme.colors.secondary};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CommentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const CommentItem = styled.div`
  background-color: rgba(255, 255, 255, 0.03);
  border-radius: 10px;
  padding: 15px;
  position: relative;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
  }
`;

const CommentContent = styled.p`
  color: ${theme.colors.secondary};
  margin-bottom: 10px;
  font-size: 0.95rem;
  word-break: break-word;
`;

const CommentMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.8rem;
  color: ${theme.colors.textSecondary};
`;

const CommentAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CommentAvatar = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
`;

const CommentDate = styled.span`
  font-size: 0.75rem;
  color: ${theme.colors.textSecondary};
`;

const CommentActions = styled.div`
  display: flex;
  gap: 10px;
`;

const CommentButton = styled.button`
  background: transparent;
  border: none;
  color: ${theme.colors.textSecondary};
  font-size: 0.8rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 3px 6px;
  border-radius: 4px;
  
  &:hover {
    color: ${theme.colors.primary};
    background-color: rgba(255, 107, 107, 0.1);
  }
`;

const CommentForm = styled.form`
  margin-top: 15px;
`;

const CommentTextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid ${theme.colors.border};
  background-color: rgba(255, 255, 255, 0.05);
  color: ${theme.colors.secondary};
  font-size: 0.9rem;
  min-height: 80px;
  resize: vertical;
  margin-bottom: 10px;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 2px ${theme.colors.primary}20;
  }
`;

const CommentSubmitButton = styled.button`
  background-color: ${theme.colors.primary};
  color: white;
  border: none;
  border-radius: 20px;
  padding: 8px 15px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${theme.colors.accent};
    transform: translateY(-2px);
  }
  
  &:disabled {
    background-color: #777;
    cursor: not-allowed;
    transform: none;
  }
`;

// Eliminar GlobalTag y añadir SourceTag
const SourceTag = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  background-color: ${props => props.color || '#666'};
  color: white;
  font-size: 0.75rem;
  border-radius: 4px;
  white-space: nowrap;
`;

// Mejorar el botón de estadísticas de reacciones
const ReactionStatsButton = styled.button`
  background: transparent;
  border: none;
  color: ${theme.colors.textSecondary};
  font-size: 0.9rem;
  padding: 5px 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  margin-left: auto;
  transition: all 0.2s ease;
  
  &:hover {
    color: ${theme.colors.primary};
    background-color: rgba(255, 107, 107, 0.1);
  }
  
  span.emoji-preview {
    display: inline-flex;
    gap: 2px;
    margin-right: 4px;
  }
  
  @media (max-width: 768px) {
    margin-left: 0;
    margin-top: 10px;
    width: 100%;
    justify-content: center;
    background-color: rgba(255, 255, 255, 0.03);
    padding: 8px 10px;
    border-radius: 8px;
    border: 1px solid ${theme.colors.border};
    font-size: 0.95rem;
    
    span.emoji-preview {
      font-size: 1.1rem;
    }
    
    &:active {
      background-color: rgba(255, 107, 107, 0.15);
    }
  }
`;

const ReactionStatsList = styled.div`
  margin-top: 10px;
  padding: 12px;
  background-color: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  border: 1px solid ${theme.colors.border};
  animation: fadeIn 0.3s ease-out;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @media (max-width: 768px) {
    padding: 15px;
    gap: 12px;
    justify-content: center;
    animation: mobileExpand 0.3s cubic-bezier(0.16, 1, 0.3, 1);
    
    @keyframes mobileExpand {
      from {
        opacity: 0.5;
        max-height: 0;
        transform: scaleY(0.8);
        transform-origin: top;
      }
      to {
        opacity: 1;
        max-height: 500px;
        transform: scaleY(1);
        transform-origin: top;
      }
    }
  }
`;

const ReactionStatsItem = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  min-width: 80px;
  justify-content: center;
  
  span.emoji {
    font-size: 1.1rem;
  }
  
  span.count {
    font-weight: 600;
    color: ${theme.colors.secondary};
  }
  
  @media (max-width: 768px) {
    min-width: 90px;
    padding: 8px 15px;
    
    span.emoji {
      font-size: 1.2rem;
    }
    
    span.count {
      font-size: 1.1rem;
    }
  }
`;

// Definir los emojis disponibles para reacciones
const AVAILABLE_REACTIONS = [
  { emoji: '❤️', icon: faHeart, name: 'heart' },
  { emoji: '👍', icon: faThumbsUp, name: 'thumbsup' },
  { emoji: '😂', icon: faLaugh, name: 'laugh' },
  { emoji: '😢', icon: faSadTear, name: 'sad' },
  { emoji: '😡', icon: faAngry, name: 'angry' }
];

// Añadir nuevos componentes estilizados para el menú flotante de reacciones
const MobileReactionsContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: ${theme.colors.backgroundAlt};
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.25);
  z-index: 1000;
  padding: 20px;
  transform: translateY(${props => props.show ? '0' : '100%'});
  transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  border-top: 1px solid ${theme.colors.border};
  max-height: 80vh;
  overflow-y: auto;
`;

const FloatingReactionsTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid ${theme.colors.border};
  
  h3 {
    color: ${theme.colors.secondary};
    margin: 0;
    font-size: 1.1rem;
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: ${theme.colors.textSecondary};
  font-size: 1.2rem;
  padding: 5px;
  cursor: pointer;
  
  &:hover, &:active {
    color: ${theme.colors.primary};
  }
`;

const MobileReactionButtonsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-bottom: 20px;
`;

const MobileReactButton = styled.button`
  background-color: ${props => props.active ? theme.colors.primary + '30' : 'rgba(255, 255, 255, 0.05)'};
  color: ${props => props.active ? theme.colors.primary : theme.colors.secondary};
  border: 1px solid ${props => props.active ? theme.colors.primary : 'transparent'};
  border-radius: 12px;
  padding: 15px 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  .emoji {
    font-size: 1.5rem;
    margin-bottom: 5px;
  }
  
  .label {
    font-size: 0.9rem;
    font-weight: ${props => props.active ? '600' : 'normal'};
  }
  
  &:hover, &:active {
    background-color: ${theme.colors.primary}20;
    border-color: ${theme.colors.primary}50;
  }
`;

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
  opacity: ${props => props.show ? 1 : 0};
  visibility: ${props => props.show ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease;
  backdrop-filter: blur(3px);
`;

const MobileReactTrigger = styled.button`
  background-color: ${theme.colors.primary};
  color: white;
  border: none;
  border-radius: 50px;
  padding: 10px 18px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 0.95rem;
  margin: 10px auto 0;
  box-shadow: 0 3px 10px rgba(255, 107, 107, 0.3);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover, &:active {
    background-color: ${theme.colors.accent};
    transform: translateY(-2px);
  }
  
  @media (min-width: 769px) {
    display: none;
  }
`;

const Secrets = () => {
  const [secrets, setSecrets] = useState([]);
  const [newSecret, setNewSecret] = useState('');
  const [nsfw, setNsfw] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [user, setUser] = useState(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [userReactions, setUserReactions] = useState({});
  const [reactionStats, setReactionStats] = useState({});
  const [showReactionStats, setShowReactionStats] = useState({});
  const [isMobile, setIsMobile] = useState(false);
  
  // Estados para la funcionalidad de comentarios
  const [comments, setComments] = useState({});
  const [loadingComments, setLoadingComments] = useState({});
  const [newComment, setNewComment] = useState({});
  const [commentError, setCommentError] = useState({});
  const [showComments, setShowComments] = useState({});
  const [submittingComment, setSubmittingComment] = useState(false);
  const [editingComment, setEditingComment] = useState(null);
  const [activeMobileReactSecret, setActiveMobileReactSecret] = useState(null);

  // Hook para detectar si es dispositivo móvil
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Comprobar inicialmente
    checkMobile();
    
    // Añadir listener para cambios de tamaño
    window.addEventListener('resize', checkMobile);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Cargar el usuario actual y los secretos al montar el componente
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          setUser(session.user);
        }
      } catch (error) {
        console.error('Error al obtener el usuario actual:', error);
      }
    };

    fetchCurrentUser();
    fetchSecrets();
  }, []);

  // Cargar las reacciones del usuario actual
  useEffect(() => {
    if (user && secrets.length > 0) {
      fetchUserReactions();
      // Cargar los comentarios para todos los secretos al inicio
      secrets.forEach(secret => {
        fetchComments(secret.id);
      });
    }
  }, [user, secrets]);

  // Función para obtener los secretos globales
  const fetchSecrets = async (loadMore = false) => {
    try {
      setLoading(true);
      const currentPage = loadMore ? page : 0;
      const pageSize = 10;
      
      const { data, error } = await supabase
        .from('secrets')
        .select('*')
        .eq('isglobal', true)
        .order('fecha', { ascending: false })
        .range(currentPage * pageSize, (currentPage + 1) * pageSize - 1);
      
      if (error) throw error;
      
      if (data.length < pageSize) {
        setHasMore(false);
      }
      
      if (loadMore) {
        setSecrets([...secrets, ...data]);
        setPage(currentPage + 1);
      } else {
        setSecrets(data);
        setPage(1);
      }
    } catch (error) {
      console.error('Error al obtener secretos:', error);
      setError('No se pudieron cargar los secretos. Por favor, intenta de nuevo más tarde.');
    } finally {
      setLoading(false);
    }
  };

  // Función para obtener las reacciones del usuario actual
  const fetchUserReactions = async () => {
    if (!user) return;
    
    try {
      const secretIds = secrets.map(secret => secret.id);
      
      const { data, error } = await supabase
        .from('secret_reactions')
        .select('*')
        .eq('userid', user.id)
        .in('secretid', secretIds);
      
      if (error) throw error;
      
      const reactions = {};
      data.forEach(reaction => {
        reactions[reaction.secretid] = reactions[reaction.secretid] || [];
        reactions[reaction.secretid].push(reaction.emoji);
      });
      
      setUserReactions(reactions);
      
      // También obtenemos estadísticas de reacciones para todos los secretos
      fetchReactionStats(secretIds);
    } catch (error) {
      console.error('Error al obtener reacciones del usuario:', error);
    }
  };

  // Nueva función para obtener estadísticas de reacciones
  const fetchReactionStats = async (secretIds) => {
    if (!secretIds?.length) return;
    
    try {
      // Obtenemos todas las reacciones para los secretos
      const { data, error } = await supabase
        .from('secret_reactions')
        .select('*')
        .in('secretid', secretIds);
      
      if (error) throw error;
      
      // Agrupar por secreto y emoji para contar
      const stats = {};
      data.forEach(reaction => {
        if (!stats[reaction.secretid]) {
          stats[reaction.secretid] = {};
        }
        
        // Incrementar el contador para este emoji
        if (!stats[reaction.secretid][reaction.emoji]) {
          stats[reaction.secretid][reaction.emoji] = 1;
        } else {
          stats[reaction.secretid][reaction.emoji]++;
        }
      });
      
      setReactionStats(stats);
    } catch (error) {
      console.error('Error al obtener estadísticas de reacciones:', error);
    }
  };

  // Función para cargar comentarios de un secreto
  const fetchComments = async (secretId) => {
    if (!secretId) return;
    
    // Evitar cargar comentarios ya cargados
    if (comments[secretId]?.length > 0) {
      console.log(`Comentarios ya cargados para secreto ${secretId}`);
      return;
    }
    
    // Evitar cargas duplicadas si ya está cargando
    if (loadingComments[secretId]) {
      console.log(`Ya está cargando comentarios para secreto ${secretId}`);
      return;
    }
    
    try {
      setLoadingComments(prev => ({ ...prev, [secretId]: true }));
      
      const { data, error } = await supabase
        .from('secret_comments')
        .select('*')
        .eq('secretid', secretId)
        .eq('is_deleted', false)
        .order('created_at', { ascending: true });
      
      if (error) throw error;
      
      setComments(prev => ({ 
        ...prev, 
        [secretId]: data || [] 
      }));
    } catch (error) {
      console.error(`Error al cargar comentarios para secreto ${secretId}:`, error);
      setCommentError(prev => ({ 
        ...prev, 
        [secretId]: 'No se pudieron cargar los comentarios' 
      }));
    } finally {
      setLoadingComments(prev => ({ ...prev, [secretId]: false }));
    }
  };

  // Función para manejar el envío de un nuevo comentario
  const handleCommentSubmit = async (e, secretId) => {
    e.preventDefault();
    
    if (!user) {
      setCommentError(prev => ({ 
        ...prev, 
        [secretId]: 'Debes iniciar sesión para comentar' 
      }));
      return;
    }
    
    const commentText = newComment[secretId];
    if (!commentText || !commentText.trim()) {
      setCommentError(prev => ({ 
        ...prev, 
        [secretId]: 'El comentario no puede estar vacío' 
      }));
      return;
    }
    
    try {
      setSubmittingComment(true);
      setCommentError(prev => ({ ...prev, [secretId]: null }));
      
      // Si estamos editando un comentario, actualizarlo
      if (editingComment && editingComment.secretid === secretId) {
        const { error } = await supabase
          .from('secret_comments')
          .update({ content: commentText.trim() })
          .eq('id', editingComment.id)
          .eq('userid', user.id);
        
        if (error) throw error;
        
        // Actualizar comentario en el estado local
        setComments(prev => {
          const updatedComments = prev[secretId].map(c => 
            c.id === editingComment.id 
              ? { ...c, content: commentText.trim(), updated_at: new Date().toISOString() }
              : c
          );
          return { ...prev, [secretId]: updatedComments };
        });
        
        // Resetear estado de edición
        setEditingComment(null);
      } else {
        // Crear nuevo comentario
        const newCommentObj = {
          secretid: secretId,
          userid: user.id,
          username: user.user_metadata?.full_name || user.user_metadata?.name || 'Usuario de Discord',
          avatar_url: user.user_metadata?.avatar_url,
          content: commentText.trim()
        };
        
        const { data, error } = await supabase
          .from('secret_comments')
          .insert([newCommentObj])
          .select();
        
        if (error) throw error;
        
        // Añadir comentario al estado local
        setComments(prev => ({
          ...prev,
          [secretId]: [...(prev[secretId] || []), data[0]]
        }));
      }
      
      // Limpiar campo de comentario
      setNewComment(prev => ({ ...prev, [secretId]: '' }));
    } catch (error) {
      console.error('Error al enviar comentario:', error);
      setCommentError(prev => ({ 
        ...prev, 
        [secretId]: 'No se pudo enviar el comentario' 
      }));
    } finally {
      setSubmittingComment(false);
    }
  };

  // Función para eliminar un comentario
  const handleDeleteComment = async (commentId, secretId) => {
    if (!user) return;
    
    try {
      // Marcar como eliminado en lugar de eliminar físicamente
      const { error } = await supabase
        .from('secret_comments')
        .update({ is_deleted: true })
        .eq('id', commentId)
        .eq('userid', user.id);
      
      if (error) throw error;
      
      // Actualizar estado local
      setComments(prev => ({
        ...prev,
        [secretId]: prev[secretId].filter(c => c.id !== commentId)
      }));
    } catch (error) {
      console.error('Error al eliminar comentario:', error);
      setCommentError(prev => ({ 
        ...prev, 
        [secretId]: 'No se pudo eliminar el comentario' 
      }));
    }
  };

  // Función para iniciar la edición de un comentario
  const startEditComment = (comment) => {
    setEditingComment(comment);
    setNewComment(prev => ({ ...prev, [comment.secretid]: comment.content }));
  };

  // Función para cancelar la edición
  const cancelEditComment = (secretId) => {
    setEditingComment(null);
    setNewComment(prev => ({ ...prev, [secretId]: '' }));
  };

  // Función para manejar los botones de mostrar/ocultar comentarios
  const toggleComments = async (secretId) => {
    const isShowing = showComments[secretId];
    
    if (!isShowing) {
      // Si no se han cargado los comentarios, cargarlos
      if (!comments[secretId]) {
        await fetchComments(secretId);
      }
    }
    
    setShowComments(prev => ({ 
      ...prev, 
      [secretId]: !prev[secretId] 
    }));
  };

  // Función para manejar el cambio del checkbox NSFW
  const handleNSFWChange = (e) => {
    const checked = e.target.checked;
    console.log('Cambiando NSFW a:', checked);
    setNsfw(checked);
  };

  // Función para enviar un nuevo secreto (actualizada, sin etiqueta global)
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newSecret.trim()) {
      setError('Por favor, escribe un secreto antes de enviarlo.');
      return;
    }
    
    if (!user) {
      setError('Debes iniciar sesión para enviar un secreto.');
      return;
    }
    
    try {
      setSubmitting(true);
      setError(null);
      
      console.log('Usuario actual:', user);
      console.log('Estado NSFW:', nsfw);
      
      // Generar UUID para el secreto
      const secretId = uuidv4();
      console.log('UUID generado:', secretId);
      
      // Crear objeto de secreto con todos los campos requeridos
      // Los secretos desde la web siempre son globales
      const newSecretObj = {
        id: secretId,
        contenido: newSecret.trim(),
        nsfw: nsfw,
        fecha: new Date().toISOString(),
        guildid: 'web', // Identificador especial para secretos desde la web
        userid: user.id,
        isglobal: true, // IMPORTANTE: Marcar como global para que se muestre en los servidores de Discord
        reaction_count: 0
      };
      
      console.log('Objeto de secreto a insertar:', newSecretObj);
      
      // Insertar el secreto en la base de datos
      const { data, error } = await supabase
        .from('secrets')
        .insert([newSecretObj])
        .select();
      
      if (error) {
        console.error('Error detallado al insertar secreto:', error);
        throw error;
      }
      
      console.log('Secreto insertado correctamente:', data);
      
      // Consultar los servidores con secretos globales habilitados
      try {
        const { data: servidores, error: errorServidores } = await supabase
          .from('server_config')
          .select('*')
          .eq('enabled', true)
          .eq('globalenabled', true);
        
        if (errorServidores) {
          console.error('Error al obtener servidores con secretos globales:', errorServidores);
        } else {
          // Verificar que hay servidores con secretos globales habilitados
          if (!servidores || servidores.length === 0) {
            console.warn('No se encontraron servidores con secretos globales habilitados');
            setSuccess('Tu secreto ha sido guardado, pero no hay servidores con secretos globales habilitados.');
          } else {
            console.log(`El secreto será sincronizado con ${servidores.length} servidores de Discord:`, 
              servidores.map(s => s.guildid));
            
            // Preparar los datos para enviar al webhook - usando el formato que sabemos que funciona
            const webhookData = {
              record: {
                id: secretId,
                contenido: newSecret.trim(),
                nsfw: nsfw,
                isglobal: true
              },
              table: "secrets"
            };
            
            console.log('Enviando datos al webhook en formato simplificado:', webhookData);
            
            // Usar XMLHttpRequest en lugar de fetch para evitar problemas con service workers
            const xhr = new XMLHttpRequest();
            xhr.open('POST', 'https://api.tusecreto.net/webhook/global-secret', true);
            xhr.setRequestHeader('Content-Type', 'application/json');
            xhr.setRequestHeader('Authorization', 'Bearer da84f483fcc54b94d3e7b736e4368782609d55dcf30f7a79');
            
            // Configurar callback para la respuesta
            xhr.onreadystatechange = function() {
              if (xhr.readyState === 4) {
                console.log('Estado respuesta XHR:', xhr.status);
                console.log('Respuesta XHR:', xhr.responseText);
                
                if (xhr.status >= 200 && xhr.status < 300) {
                  try {
                    const responseData = JSON.parse(xhr.responseText);
                    console.log('Respuesta parseada:', responseData);
                    
                    if (responseData.sent && responseData.sent > 0) {
                      // Mensaje simple sin mencionar los servidores
                      setSuccess(`¡Tu secreto ha sido enviado exitosamente!`);
                    } else {
                      setSuccess('Tu secreto ha sido guardado, pero no se pudo enviar a Discord.');
                    }
                  } catch (e) {
                    console.error('Error al parsear respuesta:', e);
                    setSuccess('Tu secreto ha sido guardado pero hubo un error al procesar la respuesta.');
                  }
                } else {
                  setSuccess('Tu secreto ha sido guardado, pero no se pudo enviar a Discord. Por favor, contacta al administrador.');
                }
              }
            };
            
            // Configurar callback para errores
            xhr.onerror = function() {
              console.error('Error de red en XMLHttpRequest');
              setSuccess('Tu secreto ha sido guardado, pero hubo un error de red al enviarlo a Discord.');
            };
            
            // Enviar la solicitud
            console.log('Enviando solicitud XHR');
            xhr.send(JSON.stringify(webhookData));
            
            // Continuar con el resto del código
            setNewSecret('');
            setNsfw(false);
            setSecrets([{...newSecretObj, is_discord_synced: true}, ...secrets]);
          }
        }
      } catch (error) {
        console.error('Error al obtener servidores:', error);
        setError('No se pudieron obtener los servidores. Por favor, intenta de nuevo más tarde.');
      }
    } catch (error) {
      console.error('Error al enviar secreto:', error);
      setError('No se pudo enviar el secreto. Por favor, intenta de nuevo más tarde.');
    }
  };

  // Función para manejar las reacciones a los secretos
  const handleReaction = async (secretId, emoji) => {
    if (!user) {
      setError('Debes iniciar sesión para reaccionar a los secretos.');
      return;
    }
    
    try {
      // Verificar si el usuario ya ha reaccionado con este emoji
      const userReactionsForSecret = userReactions[secretId] || [];
      const hasReacted = userReactionsForSecret.includes(emoji);
      
      if (hasReacted) {
        // Eliminar la reacción
        const { error } = await supabase
          .from('secret_reactions')
          .delete()
          .eq('secretid', secretId)
          .eq('userid', user.id)
          .eq('emoji', emoji);
        
        if (error) throw error;
        
        // Actualizar el estado local
        setUserReactions(prev => {
          const updated = { ...prev };
          updated[secretId] = updated[secretId].filter(e => e !== emoji);
          return updated;
        });
        
        // Actualizar el contador de reacciones en el secreto
        setSecrets(prev => 
          prev.map(secret => 
            secret.id === secretId 
              ? { ...secret, reaction_count: Math.max(0, secret.reaction_count - 1) } 
              : secret
          )
        );
        
        // Actualizar estadísticas de reacciones
        setReactionStats(prev => {
          const updated = { ...prev };
          if (updated[secretId] && updated[secretId][emoji]) {
            updated[secretId] = { ...updated[secretId] };
            updated[secretId][emoji] = Math.max(0, updated[secretId][emoji] - 1);
            if (updated[secretId][emoji] === 0) {
              delete updated[secretId][emoji];
            }
          }
          return updated;
        });
      } else {
        // Añadir la reacción
        const { error } = await supabase
          .from('secret_reactions')
          .insert([
            {
              secretid: secretId,
              userid: user.id,
              emoji: emoji,
              fecha: new Date().toISOString()
            }
          ]);
        
        if (error) throw error;
        
        // Actualizar el estado local
        setUserReactions(prev => {
          const updated = { ...prev };
          updated[secretId] = [...(updated[secretId] || []), emoji];
          return updated;
        });
        
        // Actualizar el contador de reacciones en el secreto
        setSecrets(prev => 
          prev.map(secret => 
            secret.id === secretId 
              ? { ...secret, reaction_count: secret.reaction_count + 1 } 
              : secret
          )
        );
        
        // Actualizar estadísticas de reacciones
        setReactionStats(prev => {
          const updated = { ...prev };
          if (!updated[secretId]) {
            updated[secretId] = {};
          }
          updated[secretId] = { ...updated[secretId] };
          updated[secretId][emoji] = (updated[secretId][emoji] || 0) + 1;
          return updated;
        });
      }
    } catch (error) {
      console.error('Error al gestionar la reacción:', error);
      setError('No se pudo procesar tu reacción. Por favor, intenta de nuevo más tarde.');
    }
  };

  // Función para formatear la fecha
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Función para cargar más secretos
  const loadMoreSecrets = () => {
    fetchSecrets(true);
  };

  // Función para iniciar sesión con Discord
  const handleLogin = async () => {
    try {
      setError(null);
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'discord',
        options: {
          redirectTo: window.location.origin + '/secretos'
        }
      });
      
      if (error) throw error;
      
    } catch (error) {
      console.error('Error al iniciar sesión:', error);
      setError('No se pudo iniciar sesión. Por favor, intenta de nuevo más tarde.');
    }
  };

  // Función para alternar la visualización de estadísticas
  const toggleReactionStats = (secretId) => {
    // Añadir efecto de scroll suave para móviles
    const isShowing = showReactionStats[secretId];
    
    setShowReactionStats(prev => ({
      ...prev,
      [secretId]: !prev[secretId]
    }));
    
    // Si estamos mostrando en móvil, hacer scroll suave
    if (isMobile && !isShowing) {
      // Dar tiempo a que se renderice
      setTimeout(() => {
        const element = document.getElementById(`stats-${secretId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  };

  // Función para mostrar un resumen de emojis en el botón de estadísticas
  const getEmojiSummary = (secretId) => {
    if (!reactionStats[secretId]) return null;
    
    // Ordenar emojis por cantidad (de mayor a menor)
    const sortedEmojis = Object.entries(reactionStats[secretId])
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3) // Mostrar solo los 3 más populares
      .map(([emoji]) => emoji);
    
    if (sortedEmojis.length === 0) return null;
    
    return (
      <span className="emoji-preview">
        {sortedEmojis.join(' ')}
      </span>
    );
  };

  // Nueva función para versión móvil más compacta
  const getMobileReactionButton = (secretId, totalReactions) => {
    if (!isMobile || totalReactions === 0) return null;

    // Para móviles, mostramos algo más compacto
    return (
      <span>
        {getEmojiSummary(secretId)}
        {showReactionStats[secretId] ? 'Ocultar' : 'Ver detalles'}
      </span>
    );
  };

  // Función para abrir el menú móvil de reacciones
  const openMobileReactions = (secretId) => {
    setActiveMobileReactSecret(secretId);
    // Bloquear el scroll del body
    document.body.style.overflow = 'hidden';
  };
  
  // Función para cerrar el menú móvil de reacciones
  const closeMobileReactions = () => {
    setActiveMobileReactSecret(null);
    // Restaurar el scroll del body
    document.body.style.overflow = 'auto';
  };
  
  // Función para manejar reacciones desde el menú móvil
  const handleMobileReaction = (secretId, emoji) => {
    handleReaction(secretId, emoji);
    // No cerramos el menú para permitir múltiples reacciones
  };

  return (
    <SecretsContainer>
      <PageTitle>
        <FontAwesomeIcon icon={faLock} style={{ marginRight: '15px', color: theme.colors.accent }} /> 
        Secretos Anónimos
      </PageTitle>
      
      {user ? (
        <UserInfoBadge>
          <UserAvatar 
            src={user.user_metadata?.avatar_url || 'https://cdn.discordapp.com/embed/avatars/0.png'} 
            alt="Avatar" 
          />
          <UserName>
            {user.user_metadata?.full_name || user.user_metadata?.name || 'Usuario de Discord'}
          </UserName>
        </UserInfoBadge>
      ) : (
        <LoginButton onClick={handleLogin}>
          <FontAwesomeIcon icon={faDiscord} /> Iniciar sesión con Discord
        </LoginButton>
      )}
      
      <SecretForm onSubmit={handleSubmit}>
        <FormTitle>
          <FontAwesomeIcon icon={faLock} /> Comparte tu secreto anónimo
        </FormTitle>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}
        
        <TextArea
          value={newSecret}
          onChange={(e) => setNewSecret(e.target.value)}
          placeholder="Escribe aquí tu secreto... Se compartirá en todos los servidores de Discord con secretos globales activados."
          maxLength={500}
          disabled={!user}
        />
        
        <CheckboxContainer>
          <Checkbox
            type="checkbox"
            id="nsfw"
            checked={nsfw}
            onChange={handleNSFWChange}
            disabled={!user}
          />
          <CheckboxLabel htmlFor="nsfw" isChecked={nsfw}>
            <NSFWIcon>+18</NSFWIcon> Marcar como contenido sensible (NSFW)
          </CheckboxLabel>
        </CheckboxContainer>
        
        <SubmitButton type="submit" disabled={submitting || !newSecret.trim() || !user}>
          <FontAwesomeIcon icon={faPaperPlane} /> Enviar Secreto
        </SubmitButton>
      </SecretForm>
      
      {loading && page === 0 ? (
        <LoadingSpinner>Cargando secretos...</LoadingSpinner>
      ) : (
        <>
          <SecretsList>
            {secrets.length > 0 ? (
              secrets.map((secret) => (
                <SecretCard key={secret.id}>
                  <SecretContent>{secret.contenido}</SecretContent>
                  
                  <SecretMeta>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <SecretDate>
                        {formatDate(secret.fecha)}
                      </SecretDate>
                      
                      {/* Mostrar etiqueta de Discord o Web según el origen */}
                      {secret.guildid !== 'web' ? (
                        <SourceTag color="#7289DA">
                          <FontAwesomeIcon icon={faDiscord} /> Discord
                        </SourceTag>
                      ) : (
                        <SourceTag color={theme.colors.primary}>
                          <FontAwesomeIcon icon={faComment} /> Web
                        </SourceTag>
                      )}
                    </div>
                    
                    {secret.nsfw && (
                      <span style={{ 
                        color: 'white', 
                        background: theme.colors.accent, 
                        padding: '2px 8px', 
                        borderRadius: '4px',
                        fontSize: '0.7rem'
                      }}>
                        NSFW
                      </span>
                    )}
                  </SecretMeta>
                  
                  {/* Versión de escritorio de la barra de reacciones */}
                  {!isMobile && (
                    <ReactionBar>
                      {AVAILABLE_REACTIONS.map((reaction) => {
                        const hasReacted = userReactions[secret.id]?.includes(reaction.emoji);
                        
                        return (
                          <ReactionButton
                            key={reaction.name}
                            onClick={() => handleReaction(secret.id, reaction.emoji)}
                            active={hasReacted}
                            disabled={!user}
                          >
                            {reaction.emoji} <span>{reaction.name}</span>
                          </ReactionButton>
                        );
                      })}
                      
                      {/* Botón para mostrar/ocultar comentarios */}
                      <ReactionButton
                        onClick={() => toggleComments(secret.id)}
                        active={showComments[secret.id]}
                      >
                        <FontAwesomeIcon icon={faComments} /> <span>
                          Comentarios {comments[secret.id]?.length > 0 ? `(${comments[secret.id]?.length})` : ''}
                        </span>
                      </ReactionButton>
                      
                      {/* Botón para mostrar estadísticas de reacciones */}
                      {secret.reaction_count > 0 && (
                        <ReactionStatsButton onClick={() => toggleReactionStats(secret.id)}>
                          {getEmojiSummary(secret.id)}
                          {showReactionStats[secret.id] ? 'Ocultar' : 'Ver'} {secret.reaction_count} reacciones
                        </ReactionStatsButton>
                      )}
                    </ReactionBar>
                  )}
                  
                  {/* Versión móvil con botón para desplegar menú */}
                  {isMobile && (
                    <>
                      <ReactionBar>
                        {/* Botón para mostrar/ocultar comentarios */}
                        <ReactionButton
                          onClick={() => toggleComments(secret.id)}
                          active={showComments[secret.id]}
                        >
                          <FontAwesomeIcon icon={faComments} /> <span>
                            Comentarios {comments[secret.id]?.length > 0 ? `(${comments[secret.id]?.length})` : ''}
                          </span>
                        </ReactionButton>
                        
                        {/* Botón para mostrar estadísticas de reacciones */}
                        {secret.reaction_count > 0 && (
                          <ReactionStatsButton onClick={() => toggleReactionStats(secret.id)}>
                            {getEmojiSummary(secret.id)}
                            {showReactionStats[secret.id] ? 'Ocultar' : 'Ver detalles'}
                          </ReactionStatsButton>
                        )}
                      </ReactionBar>
                      
                      {/* Botón para abrir menú flotante de reacciones */}
                      <MobileReactTrigger onClick={() => openMobileReactions(secret.id)}>
                        <FontAwesomeIcon icon={faSmile} /> Reaccionar
                      </MobileReactTrigger>
                    </>
                  )}
                  
                  {/* Mostrar estadísticas de reacciones si están habilitadas */}
                  {showReactionStats[secret.id] && reactionStats[secret.id] && (
                    <ReactionStatsList id={`stats-${secret.id}`}>
                      {Object.entries(reactionStats[secret.id])
                        .sort((a, b) => b[1] - a[1]) // Ordenar por cantidad (mayor a menor)
                        .map(([emoji, count]) => (
                          <ReactionStatsItem key={emoji}>
                            <span className="emoji">{emoji}</span>
                            <span className="count">{count}</span>
                          </ReactionStatsItem>
                        ))
                      }
                    </ReactionStatsList>
                  )}
                  
                  {/* Sección de comentarios */}
                  {showComments[secret.id] && (
                    <CommentSection>
                      <CommentHeader>
                        <CommentTitle>
                          <FontAwesomeIcon icon={faComments} /> Comentarios
                        </CommentTitle>
                      </CommentHeader>
                      
                      {/* Lista de comentarios */}
                      <CommentList>
                        {loadingComments[secret.id] ? (
                          <div style={{ textAlign: 'center', padding: '20px' }}>
                            <FontAwesomeIcon icon={faSpinner} spin /> Cargando comentarios...
                          </div>
                        ) : comments[secret.id]?.length > 0 ? (
                          comments[secret.id].map((comment) => (
                            <CommentItem key={comment.id}>
                              <CommentContent>
                                {comment.content}
                              </CommentContent>
                              <CommentMeta>
                                <CommentAuthor>
                                  <CommentAvatar 
                                    src={comment.avatar_url || 'https://cdn.discordapp.com/embed/avatars/0.png'} 
                                    alt={comment.username} 
                                  />
                                  <span>{comment.username}</span>
                                  <CommentDate>
                                    {formatDate(comment.created_at)}
                                  </CommentDate>
                                </CommentAuthor>
                                
                                {/* Acciones de comentarios (sólo para el autor) */}
                                {user && comment.userid === user.id && (
                                  <CommentActions>
                                    <CommentButton
                                      onClick={() => startEditComment(comment)}
                                      disabled={editingComment?.id === comment.id}
                                    >
                                      <FontAwesomeIcon icon={faEdit} />
                                    </CommentButton>
                                    <CommentButton
                                      onClick={() => handleDeleteComment(comment.id, secret.id)}
                                    >
                                      <FontAwesomeIcon icon={faTrash} />
                                    </CommentButton>
                                  </CommentActions>
                                )}
                              </CommentMeta>
                            </CommentItem>
                          ))
                        ) : (
                          <div style={{ textAlign: 'center', padding: '10px', color: theme.colors.textSecondary }}>
                            No hay comentarios aún. ¡Sé el primero en comentar!
                          </div>
                        )}
                      </CommentList>
                      
                      {/* Formulario para añadir comentario */}
                      {user ? (
                        <CommentForm onSubmit={(e) => handleCommentSubmit(e, secret.id)}>
                          {commentError[secret.id] && (
                            <div style={{ color: 'red', marginBottom: '10px', fontSize: '0.9rem' }}>
                              {commentError[secret.id]}
                            </div>
                          )}
                          <CommentTextArea
                            value={newComment[secret.id] || ''}
                            onChange={(e) => setNewComment({ ...newComment, [secret.id]: e.target.value })}
                            placeholder={editingComment?.id ? "Editar comentario..." : "Escribe un comentario..."}
                            maxLength={500}
                          />
                          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                            {editingComment?.secretid === secret.id && (
                              <CommentSubmitButton 
                                type="button" 
                                onClick={() => cancelEditComment(secret.id)}
                                style={{ background: '#666' }}
                              >
                                Cancelar
                              </CommentSubmitButton>
                            )}
                            <CommentSubmitButton 
                              type="submit" 
                              disabled={submittingComment || !newComment[secret.id]?.trim()}
                            >
                              <FontAwesomeIcon icon={editingComment?.secretid === secret.id ? faEdit : faPaperPlane} />
                              {editingComment?.secretid === secret.id ? 'Actualizar' : 'Comentar'}
                            </CommentSubmitButton>
                          </div>
                        </CommentForm>
                      ) : (
                        <div style={{ textAlign: 'center', padding: '10px', marginTop: '10px' }}>
                          <LoginButton onClick={handleLogin} style={{ margin: '0 auto' }}>
                            <FontAwesomeIcon icon={faDiscord} /> Inicia sesión para comentar
                          </LoginButton>
                        </div>
                      )}
                    </CommentSection>
                  )}
                </SecretCard>
              ))
            ) : (
              <NoSecrets>
                No hay secretos aún. ¡Sé el primero en compartir uno!
              </NoSecrets>
            )}
          </SecretsList>
          
          {hasMore && (
            <LoadMoreButton 
              onClick={loadMoreSecrets} 
              disabled={loading}
            >
              {loading ? 'Cargando...' : 'Cargar más secretos'}
            </LoadMoreButton>
          )}
        </>
      )}
      
      {/* Menú flotante para reacciones en móvil */}
      <Backdrop 
        show={activeMobileReactSecret !== null} 
        onClick={closeMobileReactions}
      />
      
      <MobileReactionsContainer show={activeMobileReactSecret !== null}>
        <FloatingReactionsTitle>
          <h3><FontAwesomeIcon icon={faSmile} /> Reaccionar al secreto</h3>
          <CloseButton onClick={closeMobileReactions}>
            <FontAwesomeIcon icon={faTimes} />
          </CloseButton>
        </FloatingReactionsTitle>
        
        <MobileReactionButtonsGrid>
          {AVAILABLE_REACTIONS.map((reaction) => {
            const hasReacted = userReactions[activeMobileReactSecret]?.includes(reaction.emoji);
            
            return (
              <MobileReactButton
                key={reaction.name}
                onClick={() => handleMobileReaction(activeMobileReactSecret, reaction.emoji)}
                active={hasReacted}
                disabled={!user}
              >
                <span className="emoji">{reaction.emoji}</span>
                <span className="label">{reaction.name}</span>
              </MobileReactButton>
            );
          })}
        </MobileReactionButtonsGrid>
        
        {/* Estadísticas en el panel flotante */}
        {activeMobileReactSecret && reactionStats[activeMobileReactSecret] && 
         Object.keys(reactionStats[activeMobileReactSecret]).length > 0 && (
          <>
            <div style={{ margin: '10px 0', fontSize: '0.9rem', color: theme.colors.secondary }}>
              Reacciones actuales:
            </div>
            <ReactionStatsList style={{ marginBottom: '20px' }}>
              {Object.entries(reactionStats[activeMobileReactSecret])
                .sort((a, b) => b[1] - a[1])
                .map(([emoji, count]) => (
                  <ReactionStatsItem key={emoji} style={{ minWidth: '70px' }}>
                    <span className="emoji">{emoji}</span>
                    <span className="count">{count}</span>
                  </ReactionStatsItem>
                ))
              }
            </ReactionStatsList>
          </>
        )}
      </MobileReactionsContainer>
    </SecretsContainer>
  );
};

export default Secrets; 